import { isReaddyCdnUrl } from "@/utils/media";
import * as businessesMock from "@/mocks/businesses";
import * as categoriesMock from "@/mocks/categories";
import * as touristSpotsMock from "@/mocks/touristSpots";
import * as plansMock from "@/mocks/plans";
import * as siteSettingsMock from "@/mocks/siteSettings";
import * as locationsMock from "@/mocks/locations";
import * as azCategoriesMock from "@/mocks/azCategories";
import * as adminDataMock from "@/mocks/adminData";
import * as ownerDataMock from "@/mocks/ownerData";
import { OG_IMAGES } from "@/config/ogImages";

/**
 * Full project image catalog.
 *
 * The public page-load reporter (src/utils/mockImageHost.ts) only knows the
 * images a *visitor* happens to see. The admin "Re-sync all images now" action
 * needs the COMPLETE set of Readdy CDN images the project references, so an
 * operator can prime the whole library in one go.
 *
 * Every exported value from the demo/fallback data modules is walked
 * recursively and any string that points at a Readdy CDN is collected. Walking
 * the data (instead of hard-coding field names) means new mock images are
 * picked up automatically and nothing is missed.
 */
function collect(value: unknown, out: Set<string>, depth = 0): void {
  if (value == null || depth > 8) return;
  if (typeof value === "string") {
    if (isReaddyCdnUrl(value)) out.add(value.trim());
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collect(item, out, depth + 1));
    return;
  }
  if (typeof value === "object") {
    Object.values(value as Record<string, unknown>).forEach((item) => collect(item, out, depth + 1));
  }
}

/** Every distinct Readdy CDN image URL referenced by the bundled data. */
export function collectMirrorImageUrls(): string[] {
  const out = new Set<string>();
  const sources: unknown[] = [
    businessesMock,
    categoriesMock,
    touristSpotsMock,
    plansMock,
    siteSettingsMock,
    locationsMock,
    azCategoriesMock,
    adminDataMock,
    ownerDataMock,
    OG_IMAGES,
  ];
  sources.forEach((source) => collect(source, out));
  return Array.from(out);
}